import { UcoastEl } from '@/scripts/core/UcoastEl'
import { motionEnabled } from '@/scripts/core/motion'
import { FEATURED_PRODUCT_VARIANT_CHANGE_EVENT } from '@/scripts/theme/constants'

export class GalleryCarousel extends UcoastEl {
	static htmlSelector = 'gallery-carousel'

	private cards: NodeListOf<HTMLElement>
	private total = 0
	private current = 0
	private dragStart = 0
	private dragging = false
	private departures = new Map<HTMLElement, symbol>()

	private counter: HTMLElement | null
	private prevButton: HTMLButtonElement | null
	private nextButton: HTMLButtonElement | null
	private featuredProduct: HTMLElement | null

	constructor() {
		super()

		this.cards = this.querySelectorAll<HTMLElement>('.gallery-card')
		this.total = this.cards.length

		this.counter = this.querySelector<HTMLElement>('.gallery-counter')
		this.prevButton =
			this.querySelector<HTMLButtonElement>('.gallery-arrow-left')
		this.nextButton =
			this.querySelector<HTMLButtonElement>('.gallery-arrow-right')
		this.featuredProduct = this.closest<HTMLElement>(
			'.featured-product-carousel'
		)

		this.init()
	}

	private init(): void {
		if (!this.total) return

		this.bindEvents()
		this.arrange()
	}

	private arrange(): void {
		this.cards.forEach((card, index) => {
			const offset = index - this.current
			card.inert = offset !== 0
			if (offset !== 0) {
				card.querySelectorAll('video').forEach((video) => video.pause())
			}

			if (offset < 0) {
				card.style.transform =
					'translate3d(-105%, 0, 0) rotate(-1deg)'
				card.style.opacity = '0'
				card.style.zIndex = this.departures.has(card)
					? `${this.total * 2 - index}`
					: `${index}`
			} else if (offset === 0) {
				card.style.transform =
					'translate3d(0, 0, 0) rotate(0deg)'
				card.style.opacity = '1'
				card.style.zIndex = `${this.total}`
			} else if (offset <= 2) {
				card.style.transform = `
					translate3d(${offset * 2.5}%, ${offset * -4}px, 0)
					rotate(${offset * 0.5}deg)
				`
				card.style.opacity = `${1 - offset * 0.12}`
				card.style.zIndex = `${this.total - offset}`
			} else {
				card.style.transform =
					'translate3d(7.5%, -12px, 0) rotate(1.5deg)'
				card.style.opacity = '0'
				card.style.zIndex = '0'
			}
		})

		this.updateControls()
	}

	private updateControls(): void {
		if (this.counter) {
			this.counter.textContent =
				`${this.current + 1} / ${this.total}`
		}

		if (this.prevButton) {
			this.prevButton.disabled = this.current === 0
		}

		if (this.nextButton) {
			this.nextButton.disabled =
				this.current === this.total - 1
		}
	}

	private go(index: number, animate = true): void {
		if (index < 0 || index >= this.total) return

		this.dragging = false
		this.setCardTransition(animate)
		const newDepartures: Array<[HTMLElement, symbol, number]> = []
		this.cards.forEach((card, cardIndex) => {
			if (!animate || cardIndex >= index) {
				this.departures.delete(card)
			} else if (cardIndex >= this.current) {
				const token = Symbol()
				this.departures.set(card, token)
				newDepartures.push([card, token, cardIndex])
			}
		})

		this.current = index
		this.arrange()
		newDepartures.forEach(([card, token, cardIndex]) => {
			const finish = () => {
				if (this.departures.get(card) !== token) return
				this.departures.delete(card)
				card.style.zIndex = `${cardIndex}`
			}
			const transitions = card.getAnimations().map((animation) => animation.finished)
			if (transitions.length) {
				void Promise.allSettled(transitions).then(finish)
			} else {
				finish()
			}
		})
	}

	private bindEvents(): void {
		this.featuredProduct?.addEventListener(
			FEATURED_PRODUCT_VARIANT_CHANGE_EVENT,
			this.handleVariantChange
		)

		this.prevButton?.addEventListener('click', this.handlePrevious)
		this.nextButton?.addEventListener('click', this.handleNext)
		this.addEventListener('mousedown', this.handleMouseDown)
		this.addEventListener('touchstart', this.handleTouchStart, { passive: true })

		window.addEventListener('mousemove', this.handleMouseMove)
		window.addEventListener('mouseup', this.handleMouseUp)

		window.addEventListener(
			'touchmove',
			this.handleTouchMove,
			{ passive: true }
		)

		window.addEventListener('touchend', this.handleTouchEnd)
		window.addEventListener('touchcancel', this.handleTouchCancel)

		this.addEventListener('keydown', this.handleKeydown)
		this.addEventListener('shopify:block:select', this.handleBlockSelect)
	}

	private handlePrevious = (event: MouseEvent): void => {
		this.go(this.current - 1, event.detail !== 0)
	}

	private handleNext = (event: MouseEvent): void => {
		this.go(this.current + 1, event.detail !== 0)
	}

	private startDrag(clientX: number, target: EventTarget | null): void {
		if ((target as HTMLElement)?.closest('button, a, input, select, textarea')) return
		this.departures.clear()
		this.setCardTransition(false)
		this.arrange()
		this.dragging = true
		this.dragStart = clientX
	}

	private handleMouseDown = (event: MouseEvent): void => {
		if (event.button !== 0) return
		this.startDrag(event.clientX, event.target)
	}

	private handleTouchStart = (event: TouchEvent): void => {
		if (event.touches.length !== 1) return
		this.startDrag(event.touches[0].clientX, event.target)
	}

	private handleTouchCancel = (): void => {
		if (this.dragging) this.go(this.current)
	}

	private handleMouseMove = (event: MouseEvent): void => {
		if (!this.dragging) return

		this.updateDrag(event.clientX)
	}

	private handleTouchMove = (event: TouchEvent): void => {
		if (!this.dragging) return

		this.updateDrag(event.touches[0].clientX)
	}

	private updateDrag(clientX: number): void {
		const diff = clientX - this.dragStart
		const activeCard = this.cards[this.current]

		if (!activeCard) return

		activeCard.style.transform = `
			translate3d(${diff}px, 0, 0)
			rotate(${diff * 0.03}deg)
		`
	}

	private handleMouseUp = (event: MouseEvent): void => {
		this.dragEnd(event.clientX)
	}

	private handleTouchEnd = (event: TouchEvent): void => {
		this.dragEnd(event.changedTouches[0].clientX)
	}

	private dragEnd(endX: number): void {
		if (!this.dragging) return

		this.dragging = false

		const diff = endX - this.dragStart

		let index = this.current
		if (diff < -60 && this.current < this.total - 1) {
			index++
		} else if (diff > 60 && this.current > 0) {
			index--
		}

		this.go(index)
	}

	private setCardTransition(animate = true): void {
		const transition = !animate
			? 'none'
			: motionEnabled()
			? 'transform 0.75s cubic-bezier(.645, .045, .355, 1), opacity 0.75s cubic-bezier(.645, .045, .355, 1)'
			: 'opacity 0.2s ease'

		this.cards.forEach((card) => {
			card.style.transition = transition
		})
	}

	private handleKeydown = (event: KeyboardEvent): void => {
		if (event.target !== this) return
		if (event.key === 'ArrowLeft') {
			event.preventDefault()
			this.go(this.current - 1, false)
		}

		if (event.key === 'ArrowRight') {
			event.preventDefault()
			this.go(this.current + 1, false)
		}
	}

	private handleVariantChange = (event: Event): void => {
		const variantId = (event as CustomEvent<{ variantId?: string | number }>).detail
			?.variantId

		if (variantId === undefined || variantId === null || variantId === '') return
		const normalizedVariantId = String(variantId)

		const index = Array.from(this.cards).findIndex((card) =>
			(card.dataset.variantIds ?? '')
				.split(',')
				.map((id) => id.trim())
				.includes(normalizedVariantId)
		)

		if (index >= 0) this.go(index)
	}

	private handleBlockSelect = (event: Event): void => {
		const card = (event.target as HTMLElement)?.closest('.gallery-card')
		const index = Array.from(this.cards).findIndex((item) => item === card)
		if (index >= 0) this.go(index, false)
	}

	override disconnectedCallback(): void {
		this.departures.clear()
		this.dragging = false
		this.setCardTransition(false)
		this.arrange()
		this.prevButton?.removeEventListener('click', this.handlePrevious)
		this.nextButton?.removeEventListener('click', this.handleNext)
		this.removeEventListener('mousedown', this.handleMouseDown)
		this.removeEventListener('touchstart', this.handleTouchStart)
		this.featuredProduct?.removeEventListener(
			FEATURED_PRODUCT_VARIANT_CHANGE_EVENT,
			this.handleVariantChange
		)
		window.removeEventListener('mousemove', this.handleMouseMove)
		window.removeEventListener('mouseup', this.handleMouseUp)
		window.removeEventListener('touchmove', this.handleTouchMove)
		window.removeEventListener('touchend', this.handleTouchEnd)
		window.removeEventListener('touchcancel', this.handleTouchCancel)
		this.removeEventListener('keydown', this.handleKeydown)
		this.removeEventListener('shopify:block:select', this.handleBlockSelect)
		super.disconnectedCallback()
	}
}
